import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Divider,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { FC, useCallback, useState } from 'react';

import { BoxRow } from 'src/components/BoxRow';
import { Checkbox } from 'src/components/Checkbox';
import { Dialog } from 'src/components/Dialog';
import { Spacer } from 'src/components/Spacer';
import { useAlert } from 'src/services/alert/useAlert';
import { isEmpty, isNil, maybeCall } from 'src/utils/common';
import { theme } from '../../theme';
import { useUpdateUsersBulkMutation } from '../api';
import { UserRoles } from '../constants';
import { useRoleOptions } from '../useRoleOptions';
import { isAdmin } from '../utils';
import { ConfirmAdminRoleModal } from './ConfirmAdminRoleModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (role: string) => void;
  userIds: number[];
}

export const ChangeRoleModal: FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  userIds
}) => {
  const { roleOptions } = useRoleOptions({ omitValues: [UserRoles.SUPER_ADMIN] });
  const [role, setRole] = useState<string | null>(null);
  const [isAdminConfirmOpen, setIsAdminConfirmOpen] = useState(false);
  const [updateRole, { isLoading }] = useUpdateUsersBulkMutation();
  const { onOpenAlertDelayed } = useAlert();

  const onSelectRole = useCallback(
    (selectedRole: string) => () => {
      setRole(role === selectedRole ? null : selectedRole);
    },
    [role],
  );

  const onSubmit = useCallback(async () => {
    if (!role) return;

    if (isAdmin(role)) {
      setIsAdminConfirmOpen(true);
      return;
    }

    try {
      await updateRole({ role, userIds }).unwrap();
      onClose();
      maybeCall(onSuccess, role);
      onOpenAlertDelayed({
        message: 'Role for selected users was successfully updated.',
        title: 'Role updated!',
        type: 'success',
      });
    } catch (err: any) {
      onOpenAlertDelayed({
        message: err?.data?.message === 'AT_LEAST_ONE_SUPER_ADMIN_REQUIRED'
          ? 'At least one Super Admin must remain in the system.'
          : 'There was an error, please try again.',
        title: 'Oops!',
        type: 'error',
      });
    }
  }, [role, userIds, updateRole, onClose, onSuccess, onOpenAlertDelayed]);

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        sx={{
          padding: '25px 100px',
          [theme.breakpoints.down('mobile')]: {
            margin: '15px',
          },
        }}
        title="Change role"
      >
        <List
          sx={{
            maxHeight: '375px',
            overflowX: 'auto',
          }}
        >
          {roleOptions.map((option) => (
            <Box key={option.value}>
              <ListItem
                disablePadding
                sx={{
                  height: '42px',
                  justifyContent: 'space-between',
                  paddingLeft: 0,
                  paddingRight: '5px',
                  marginRight: '10px',
                  borderBottom: '1px solid #E6E8F0',
                }}
              >
                <Typography
                  sx={{
                    color: '#356482',
                    fontSize: '16px',
                    fontWeight: '600',
                  }}
                >
                  {option.title}
                </Typography>
                <Checkbox
                  checked={option.value === role}
                  onClick={onSelectRole(option.value)}
                />
              </ListItem>
            </Box>
          ))}
        </List>
        <BoxRow sx={{ marginTop: '15px', width: '100%' }}>
          <Button
            onClick={onClose}
            sx={{ flex: 1, height: '40px' }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Spacer width="20px" />
          <LoadingButton
            disabled={isNil(role) || isEmpty(userIds)}
            loading={isLoading}
            onClick={onSubmit}
            sx={{ flex: 1, height: '40px' }}
            variant="contained"
          >
            Submit
          </LoadingButton>
        </BoxRow>
      </Dialog>
      <ConfirmAdminRoleModal
        isOpen={isAdminConfirmOpen}
        onClose={() => setIsAdminConfirmOpen(false)}
        onSuccess={(confirmedRole) => {
          onClose();
          maybeCall(onSuccess, confirmedRole);
        }}
        userIds={userIds}
        role={role || ''}
      />
    </>
  );
};
